import { Ellipsis } from 'lucide-react';
import system_setting_icon from 'images/setting_icons/system_setting_icon.svg';
import * as React from 'react';
import { useState } from 'react';
import { useHistory } from 'react-router';
import Add from '../../../components_v2/add/Add';
import Dropdown from '../../../components_v2/dropdown/Dropdown';
import { DropdownData } from '../../../components_v2/dropdown/model/Model';
import { Column, Table } from '../../../components_v2/table/Table';
import { GreySidely3 } from '../../../styles/global/css/Utils';
import { translateToString } from '../../../styles/global/translate';
import { AlertContext, AlertRes } from '../../alert/AlertProvider';
import { ToolbarImageContainer } from '../../globals/defaultToolbar/style/Style';
import { ToolbarState } from '../../globals/mainPage/mainPage';
import { TableRow } from '../../orders/templateOrders/style/Style';
import { getLang } from '../../reports/utils';
import { deleteFormEmail, FormEmailListItem, FormEmailRecipient, getFormEmailsList } from './actions';

function recipientToString(recipient: FormEmailRecipient): string {
	if (typeof recipient === 'string') {
		if (recipient === 'CompanyEmail') return translateToString('company_email');
		if (recipient === 'UserEmail') return translateToString('user_email');
		return recipient;
	}
	if ('Email' in recipient) return recipient.Email;
	return `${translateToString('additional_columns')} ${recipient.AdditionalColumn}`;
}

export default function EmailsV2Settings({ setToolBarState }: { setToolBarState: (value: ToolbarState) => void }) {
	const history = useHistory();
	const [formEmails, setFormEmails] = useState<FormEmailListItem[]>([]);
	const { alertDelete } = React.useContext(AlertContext);

	const refresh = React.useCallback(() => getFormEmailsList().then(setFormEmails), []);

	React.useEffect(() => {
		refresh();
		setToolBarState({
			bottomRightToolbarComponent: <Add size='22px' onClick={() => history.push({ pathname: '/email-v2-template' })} />
		});
	}, []);

	const list: DropdownData[] = [
		{
			label: translateToString('global.quick_action.edit'),
			value: 'EDIT'
		},
		{
			label: translateToString('global.quick_action.delete'),
			value: 'DELETE'
		}
	];

	const columns: Array<Column<FormEmailListItem>> = React.useMemo(() => {
		function toCustomLocaleDateString(date: string | null) {
			if (date === null) return '-';
			return new Date(date).toLocaleString(
				getLang(), {
					month: 'short', year: 'numeric', day: 'numeric'
				}
			);
		}

		return [
			{
				id: 'linked_form_template_view',
				Header: translateToString('linked_form_template_view'),
				accessor: (row: FormEmailListItem) => <TableRow>{row.view_name ?? '-'}</TableRow>,
				width: undefined
			},
			{
				id: 'recipient',
				Header: translateToString('recipient'),
				accessor: (row: FormEmailListItem) => <TableRow>{row.emails.length ? row.emails.map(recipientToString).join(', ') : translateToString('no_recipient')}</TableRow>,
				width: undefined
			},
			{
				id: 'linked_form',
				Header: translateToString('linked_form'),
				accessor: (row: FormEmailListItem) => <TableRow>{row.form_name}</TableRow>,
				width: undefined
			},
			{
				id: 'created_at',
				Header: translateToString('created_at'),
				accessor: (row: FormEmailListItem) => <TableRow>{toCustomLocaleDateString(row.created_at)}</TableRow>,
				width: undefined
			},
			{
				id: 'updated_at',
				Header: translateToString('updated_at'),
				accessor: (row: FormEmailListItem) => <TableRow>{toCustomLocaleDateString(row.updated_at)}</TableRow>,
				width: undefined
			},
			{
				id: 'quick_actions',
				noHeaderEllipsis: true,
				Header: <img src={system_setting_icon} style={{ width: 18 }} />,
				accessor: (row: FormEmailListItem) =>
					<Dropdown
						name='quick_action'
						datalist={list}
						readOnly
						searchable={false}
						dropdownStyle={{ width: '10px', optionWidth: '100px', fontSize: 13, optionLeft: '-112px', containerTop: '-6px' }}
						JSXButton={() =>
							<ToolbarImageContainer rotate={'90deg'} marginLeft='-3px'><Ellipsis color={GreySidely3} strokeWidth={2.5} size={24} /></ToolbarImageContainer>
						}
						onChange={value => {
							if (value.value === 'DELETE') {
								alertDelete({
									zIndex: 201,
									name: row.form_name
								})
									.then(result => result.res === AlertRes.Ok ? deleteFormEmail(row.id).then(() => refresh()) : undefined);
							}
							else if (value.value === 'EDIT') {
								history.push({
									pathname: 'email-v2-template',
									search: `?id=${row.id}`
								});
							}
						}}
					/>,
				width: 40,
				minWidth: 40,
				disableSortBy: true,
				disableFilter: true,
				unresizeable: true,
				freeze: 'right'
			}
		];
	}, [formEmails]);

	return <Table noBorder height={'100%'} columns={columns} data={formEmails} />;
}
